import { createRouter, createWebHistory } from "vue-router";
import { nextTick } from "vue";
import { useRequestsStore } from "@/stores/storeRequests";
import { useLangStore } from "@/stores/storeLang";
import { useSettingsStore } from "@/stores/storeSettings";

const TheCodePage    = () => import("@/pages/TheCodePage.vue");
const ThePlayPage    = () => import("@/pages/ThePlayPage.vue");
const TheDeliveryPage   = () => import("@/pages/TheDeliveryPage.vue");
const TheDeliveryDetailPage   = () => import("@/pages/TheDeliveryDetailPage.vue");
const TheDeliveryClaimPage   = () => import("@/pages/TheDeliveryClaimPage.vue");
const ThePrizesPage  = () => import("@/pages/ThePrizesPage.vue");
const TheAboutPage   = () => import("@/pages/TheAboutPage.vue");
const TheFaqPage     = () => import("@/pages/TheFaqPage.vue");
const TheProfilePage = () => import("@/pages/TheProfilePage.vue");
const TheWarningPage = () => import("@/pages/TheWarningPage.vue");
const TheNotFindPage = () => import("@/pages/TheNotFindPage.vue");
const TheCertPage = () => import("@/pages/TheCertPage.vue");
const TheCustomizationPage = () => import("@/pages/TheCustomizationPage.vue");
const TheNotUserPage = () => import("@/pages/TheNotUserPage.vue");
const TheSuccessApplicationPage = () => import("@/pages/TheSuccessApplicationPage.vue");
const TheSuccessMerchPage = () => import("@/pages/TheSuccessMerchPage.vue");
const TheErrorCustomPage = () => import("@/pages/TheErrorCustomPage.vue");

const router = createRouter({
	history: createWebHistory(import.meta.env.BASE_URL),
	routes: [
		// Точка входа от бота: /?section=<секция>&lang=<ru|uz>
		// Редиректит на нужный path по значению section
		{
			path: "/",
			redirect: (to) => {
				const section = to.query.section;
				const sectionMap = {
					about:            { name: "about" },
					prizes:           { name: "prizes" },
					profile:          { name: "profile" },
					code:             { name: "home" },
					faq:              { name: "faq" },
					wardrobe:         { name: "play" },
					"tshirt-customize": { name: "customization" },
				};
				return sectionMap[section] ?? { name: "home" };
			},
		},
		{
			path: "/code",
			name: "home",
			component: TheCodePage,
			meta: { requiresAuth: true },
		},
		{
			path: "/wardrobe",
			name: "play",
			component: ThePlayPage,
			meta: { requiresAuth: true },
		},
		{
			path: "/delivery",
			name: "delivery",
			component: TheDeliveryPage,
			meta: { requiresAuth: true, requiresWin: true },
		},
		{
			path: "/delivery-detail",
			name: "delivery-detail",
			component: TheDeliveryDetailPage,
			meta: { requiresAuth: true, requiresWin: true },
		},
		{
			path: "/claim",
			name: "claim",
			component: TheDeliveryClaimPage,
			meta: { requiresAuth: true, requiresWin: true },
		},
		{
			path: "/prizes",
			name: "prizes",
			component: ThePrizesPage,
		},
		{
			path: "/about",
			name: "about",
			component: TheAboutPage,
		},
		{
			path: "/faq",
			name: "faq",
			component: TheFaqPage,
		},
		{
			path: "/profile",
			name: "profile",
			component: TheProfilePage,
			meta: { requiresAuth: true },
		},
		{
			path: "/warning",
			name: "warning",
			component: TheWarningPage,
		},
		{
			path: "/warning-custom",
			redirect: { name: "warning", query: { type: "custom" } },
		},
		{
			path: "/success",
			name: "success",
			component: TheSuccessApplicationPage,
			meta: { requiresAuth: true },
		},
		{
			path: "/success-merch",
			name: "success-merch",
			component: TheSuccessMerchPage,
			meta: { requiresAuth: true },
		},
		{
			path: "/certificate",
			name: "certificate",
			component: TheCertPage,
			meta: { requiresAuth: true },
		},
		{
			path: "/tshirt-customize",
			name: "customization",
			component: TheCustomizationPage,
			meta: { requiresAuth: true, requiresWin: true },
		},
		{
			path: "/error-custom",
			name: "error-custom",
			component: TheErrorCustomPage,
			meta: { requiresAuth: true },
		},
		{
			path: "/not-user",
			name: "not-user",
			component: TheNotUserPage,
		},
		{
			path: "/:pathMatch(.*)*",
			name: "404",
			component: TheNotFindPage,
		},
	],
	scrollBehavior() {
		return { top: 0 }
	},
});

router.beforeEach(async (to, _from, next) => {
	const DEV_BYPASS = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';
	const storeRequests = useRequestsStore();

	const { ref, lang } = to.query;
	if (Object.keys(to.query).length) {
		const params = new URLSearchParams(to.query).toString();
		localStorage.setItem("registration_params", params);
	}
	if (ref) localStorage.setItem("ref", ref);
	if (lang && (lang === "ru" || lang === "uz")) {
		useLangStore().setBotLang(lang);
	}

	if (!DEV_BYPASS) {
		const requiresAuth = to.matched.some((r) => r.meta.requiresAuth);
		if (requiresAuth) {
			// Ждём initData перед каждым защищённым переходом — при навигации
			// между страницами SDK может ещё не вернуть данные
			await storeRequests.waitForInitData();
			// fetchProfile кэширует результат — повторный вызов не делает запрос
			const profile = await storeRequests.fetchProfile();
			if (!profile) {
				next({ name: "not-user" });
				return;
			}
		}

		if (to.meta.requiresCode || to.meta.requiresWin) {
			const { usePrizeStore } = await import("@/stores/storePrize.js");
			const storePrize = usePrizeStore();
			if (to.meta.requiresCode && !storePrize.codeVerified) { next({ name: "home" }); return; }
			if (to.meta.requiresWin  && !storePrize.hasWon)       { next({ name: "play" }); return; }
		}
	}

	next();
});

router.afterEach(async () => {
	useRequestsStore().responseError = "";

	const storeSettings = useSettingsStore();
	storeSettings.resetPageLoaded();

	await nextTick();

	const imgs = [...document.querySelectorAll('img')].filter(img => !img.complete);

	if (!imgs.length) {
		storeSettings.markPageLoaded();
		return;
	}

	const timeout = new Promise(resolve => setTimeout(resolve, 5000));
	const loaded = Promise.all(
		imgs.map(img => new Promise(resolve => {
			img.addEventListener('load',  resolve, { once: true });
			img.addEventListener('error', resolve, { once: true });
		}))
	);
	await Promise.race([loaded, timeout]);
	storeSettings.markPageLoaded();
});

export default router;
